import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { db, getCollectionName } from './firebase';
import { collection, query, onSnapshot, orderBy } from './firestore-compat';

// ── Normalização legado ────────────────────────────────────────────────────────
const nf = v => v === 'Homesenior' ? 'Vitalcare' : (v || '');

// ── Paleta dark (estilo telefonia) ────────────────────────────────────────────
const C = {
  bg:     '#0f172a',
  card:   'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.10)',
  text:   '#f1f5f9',
  muted:  '#94a3b8',
  dim:    '#64748b',
  green:  '#10b981',
  red:    '#ef4444',
  blue:   '#6366f1',
  purple: '#8b5cf6',
  orange: '#f59e0b',
  teal:   '#06b6d4',
  rose:   '#f43f5e',
  yellow: '#eab308',
};

const CHART_COLORS = [C.blue, C.green, C.orange, C.red, C.purple, C.teal, C.rose, C.yellow, '#ec4899', '#14b8a6'];

const S = {
  page:     { minHeight: '100vh', background: `linear-gradient(135deg,${C.bg} 0%,#1e293b 60%,${C.bg} 100%)`, padding: '28px 24px', fontFamily: "'Inter','Segoe UI',sans-serif", color: C.text },
  card:     { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px 22px', backdropFilter: 'blur(10px)' },
  secTitle: { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.blue, marginBottom: 14 },
  th:       { padding: '9px 12px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, color: C.muted, fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(255,255,255,0.02)' },
  td:       { padding: '8px 12px', borderBottom: `1px solid rgba(255,255,255,0.04)`, color: '#cbd5e1', fontSize: '0.82rem' },
  sel:      { padding: '7px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: `1px solid ${C.border}`, color: C.text, fontSize: '0.82rem', cursor: 'pointer', outline: 'none' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function calcProximaTroca(ultimaData, periodicidade) {
  if (!ultimaData || !periodicidade) return null;
  const DIAS = { 'Diária': 1, 'Semanal': 7, 'Quinzenal': 14, '28 dias': 28, 'Mensal': 30 };
  const dias = DIAS[periodicidade];
  if (!dias) return null;
  try {
    const d = new Date(ultimaData.length === 10 ? ultimaData + 'T00:00' : ultimaData);
    if (isNaN(d)) return null;
    d.setDate(d.getDate() + dias);
    return d;
  } catch { return null; }
}

function diasParaTroca(proxima) {
  if (!proxima) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.round((proxima - hoje) / 86400000);
}

function mesKey(d) {
  if (!d) return null;
  try {
    const dt = new Date(String(d).length === 10 ? d + 'T00:00' : d);
    if (isNaN(dt)) return null;
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
  } catch { return null; }
}

function fmtMes(key) {
  if (!key) return '';
  const [y, m] = key.split('-');
  const nomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${nomes[+m - 1]}/${y.slice(2)}`;
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(String(s).length === 10 ? s + 'T00:00' : s);
    return d.toLocaleDateString('pt-BR');
  } catch { return s; }
}

// ── Sub-componentes ───────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color, warn, onClick, active }) {
  return (
    <div onClick={onClick} style={{
      ...S.card,
      borderLeft: `4px solid ${color}`,
      position: 'relative', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
      outline: active ? `1px solid ${color}` : 'none',
      transition: 'outline 0.15s',
    }}>
      <div style={{ position: 'absolute', right: -8, top: -8, fontSize: '3rem', opacity: 0.07 }}>{icon}</div>
      <div style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.9rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: warn ? C.red : C.dim, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children, style = {} }) {
  return (
    <div style={{ ...S.card, marginBottom: 20, ...style }}>
      {title && <div style={S.secTitle}>{title}</div>}
      {children}
    </div>
  );
}

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', color: '#e2e8f0' }}>
      {label && <div style={{ color: C.muted, marginBottom: 4, fontWeight: 700 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || C.blue }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('pt-BR') : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

function Badge({ label, color }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
      background: `${color}22`, color, border: `1px solid ${color}44` }}>{label}</span>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────

export default function DashboardFeridas() {
  const [pacientes,  setPacientes]  = useState([]);
  const [lesoes,     setLesoes]     = useState([]);
  const [evolucoes,  setEvolucoes]  = useState([]);
  const [loading,    setLoading]    = useState(true);

  const [filtroFilial, setFiltroFilial] = useState('');
  const [filtroSit,    setFiltroSit]    = useState('');
  const [drillLabel,   setDrillLabel]   = useState(null);
  const [drillRows,    setDrillRows]    = useState([]);

  useEffect(() => {
    const colPac = getCollectionName('lesoes_pacientes');
    const colLes = getCollectionName('lesoes_lesoes');
    const colEvo = getCollectionName('lesoes_evolucoes');

    let loaded = 0;
    const done = () => { loaded++; if (loaded === 3) setLoading(false); };

    const u1 = onSnapshot(query(collection(db, colPac), orderBy('createdAt', 'desc')), s => {
      setPacientes(s.docs.map(d => ({ id: d.id, ...d.data() }))); done();
    });
    const u2 = onSnapshot(query(collection(db, colLes), orderBy('createdAt', 'asc')), s => {
      setLesoes(s.docs.map(d => ({ id: d.id, ...d.data() }))); done();
    });
    const u3 = onSnapshot(query(collection(db, colEvo), orderBy('data', 'desc')), s => {
      setEvolucoes(s.docs.map(d => ({ id: d.id, ...d.data() }))); done();
    });
    return () => { u1(); u2(); u3(); };
  }, []);

  // Enriquecimento: junta paciente + lesões + status de troca
  const pacientesRicos = useMemo(() => pacientes.map(p => {
    const minhasLesoes = lesoes.filter(l => l.pacienteId === p.id).map(l => {
      const evs = evolucoes.filter(e => e.lesaoId === l.id).sort((a, b) => (b.data || '').localeCompare(a.data || ''));
      const ultimaData = evs[0]?.data || l.data_primeira_avaliacao;
      const proxima    = calcProximaTroca(ultimaData, l.periodicidade_troca);
      const dias       = diasParaTroca(proxima);
      return { ...l, ultimaData, proxima, dias, evolucoes: evs };
    });
    return { ...p, lesoes: minhasLesoes };
  }), [pacientes, lesoes, evolucoes]);

  // Filtros base
  const base = useMemo(() => pacientesRicos.filter(p => {
    if (filtroFilial && nf(p.filial) !== filtroFilial) return false;
    if (filtroSit && p.situacao !== filtroSit) return false;
    return true;
  }), [pacientesRicos, filtroFilial, filtroSit]);

  // ── KPIs ─────────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const atendimento  = base.filter(p => p.situacao !== 'Hospitalizado' && p.situacao !== 'Alta da comissão' && p.situacao !== 'Alta do Homecare');
    const hosp         = base.filter(p => p.situacao === 'Hospitalizado');
    const alta         = base.filter(p => p.situacao === 'Alta da comissão');
    const altaHomecare = base.filter(p => p.situacao === 'Alta do Homecare');
    const totalLesoes  = base.reduce((acc, p) => acc + p.lesoes.length, 0);
    const trocasHoje   = base.flatMap(p => p.lesoes).filter(l => l.dias !== null && l.dias >= 0 && l.dias <= 1);
    const trocasAtraso = base.flatMap(p => p.lesoes).filter(l => l.dias !== null && l.dias < 0);
    const trocas7d     = base.flatMap(p => p.lesoes).filter(l => l.dias !== null && l.dias >= 0 && l.dias <= 7);
    return { total: base.length, atendimento: atendimento.length, hosp: hosp.length, alta: alta.length, altaHomecare: altaHomecare.length,
             totalLesoes, trocasHoje: trocasHoje.length, trocasAtraso: trocasAtraso.length, trocas7d: trocas7d.length };
  }, [base]);

  // ── Por filial ────────────────────────────────────────────────────────────────
  const porFilial = useMemo(() => {
    const map = {};
    base.forEach(p => { const f = nf(p.filial) || 'Não informado'; map[f] = (map[f] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Por situação ──────────────────────────────────────────────────────────────
  const SIT_COLORS = { 'Em atendimento': C.green, 'Hospitalizado': C.red, 'Alta da comissão': C.blue, 'Alta do Homecare': C.purple };
  const porSit = useMemo(() => {
    const map = {};
    base.forEach(p => { const s = p.situacao || 'Em atendimento'; map[s] = (map[s] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Tipos de lesão ────────────────────────────────────────────────────────────
  const porTipo = useMemo(() => {
    const map = {};
    base.flatMap(p => p.lesoes).forEach(l => {
      const t = l.tipo_lesao || 'Não informado';
      map[t] = (map[t] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Por periodicidade ─────────────────────────────────────────────────────────
  const porPeriodicidade = useMemo(() => {
    const map = {};
    base.flatMap(p => p.lesoes).forEach(l => {
      const t = l.periodicidade_troca || 'Não informado';
      map[t] = (map[t] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Por programa ─────────────────────────────────────────────────────────────
  const porPrograma = useMemo(() => {
    const map = {};
    base.forEach(p => { const t = nf(p.programa_atual) || 'Não informado'; map[t] = (map[t] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Por origem da lesão (Interna/Externa) ──────────────────────────────────────
  const ORIGEM_COLORS = { 'Interna': C.blue, 'Externa': C.orange };
  const porOrigem = useMemo(() => {
    const map = {};
    base.forEach(p => { const o = p.origem_lesao || 'Não informado'; map[o] = (map[o] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Por operadora ────────────────────────────────────────────────────────────
  const porOperadora = useMemo(() => {
    const map = {};
    base.forEach(p => { const o = (p.operadora || '').trim() || 'Não informado'; map[o] = (map[o] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10);
  }, [base]);

  // ── Por estádio da lesão ─────────────────────────────────────────────────────
  const porEstadio = useMemo(() => {
    const map = {};
    base.flatMap(p => p.lesoes).forEach(l => {
      const e = l.estadio || 'Não informado';
      map[e] = (map[e] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [base]);

  // ── Trocas próximas (detalhes) ────────────────────────────────────────────────
  const trocasProximas = useMemo(() => {
    const rows = [];
    base.forEach(p => {
      p.lesoes.forEach(l => {
        if (l.dias !== null && l.dias <= 7) {
          rows.push({ pac: p.nome, filial: nf(p.filial), tipo: l.tipo_lesao, periodicidade: l.periodicidade_troca,
                       proxima: l.proxima, dias: l.dias });
        }
      });
    });
    return rows.sort((a, b) => (a.dias ?? 999) - (b.dias ?? 999));
  }, [base]);

  // ── Evolução mensal de pacientes ativos ───────────────────────────────────────
  const evolucaoMensal = useMemo(() => {
    const map = {};
    pacientesRicos.forEach(p => {
      const mk = mesKey(p.createdAt?.seconds ? new Date(p.createdAt.seconds * 1000).toISOString().slice(0, 10) : p.createdAt);
      if (!mk) return;
      if (!map[mk]) map[mk] = { mes: mk, pacientes: 0, lesoes: 0 };
      map[mk].pacientes++;
      map[mk].lesoes += p.lesoes.length;
    });
    return Object.values(map).sort((a, b) => a.mes.localeCompare(b.mes)).slice(-12)
      .map(r => ({ ...r, mes: fmtMes(r.mes) }));
  }, [pacientesRicos]);

  const filiais = useMemo(() => [...new Set(pacientesRicos.map(p => nf(p.filial)).filter(Boolean))].sort(), [pacientesRicos]);

  const openDrill = (label, rows) => {
    if (drillLabel === label) { setDrillLabel(null); setDrillRows([]); }
    else { setDrillLabel(label); setDrillRows(rows); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', background: C.bg }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🩹</div>
        <div style={{ color: C.blue, fontWeight: 700 }}>Carregando dashboard...</div>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>🩹 Dashboard de Feridas</h1>
            <p style={{ margin: '4px 0 0', color: C.muted, fontSize: '0.83rem' }}>Gestão de pacientes, lesões e trocas de curativo</p>
          </div>
          {/* Filtros */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select value={filtroFilial} onChange={e => setFiltroFilial(e.target.value)} style={S.sel}>
              <option value="">Todas as filiais</option>
              {filiais.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select value={filtroSit} onChange={e => setFiltroSit(e.target.value)} style={S.sel}>
              <option value="">Todas as situações</option>
              {['Em atendimento', 'Hospitalizado', 'Alta da comissão', 'Alta do Homecare'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(filtroFilial || filtroSit) && (
              <button onClick={() => { setFiltroFilial(''); setFiltroSit(''); }}
                style={{ ...S.sel, color: C.muted, cursor: 'pointer' }}>✕ Limpar</button>
            )}
          </div>
        </div>

        {/* ── KPIs ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, marginBottom: 22 }}>
          <KpiCard icon="👤" label="Total de pacientes"  value={kpis.total}        color={C.blue}   sub="no filtro atual" />
          <KpiCard icon="🟢" label="Em atendimento"      value={kpis.atendimento}  color={C.green}  sub="ativos"
            onClick={() => openDrill('Em atendimento', base.filter(p => p.situacao !== 'Hospitalizado' && p.situacao !== 'Alta da comissão' && p.situacao !== 'Alta do Homecare'))}
            active={drillLabel === 'Em atendimento'} />
          <KpiCard icon="🏥" label="Hospitalizados"      value={kpis.hosp}         color={C.red}
            onClick={() => openDrill('Hospitalizados', base.filter(p => p.situacao === 'Hospitalizado'))}
            active={drillLabel === 'Hospitalizados'} />
          <KpiCard icon="✅" label="Alta da comissão"    value={kpis.alta}         color={C.blue}
            onClick={() => openDrill('Alta da comissão', base.filter(p => p.situacao === 'Alta da comissão'))}
            active={drillLabel === 'Alta da comissão'} />
          <KpiCard icon="🏠" label="Alta do Homecare"    value={kpis.altaHomecare} color={C.purple}
            onClick={() => openDrill('Alta do Homecare', base.filter(p => p.situacao === 'Alta do Homecare'))}
            active={drillLabel === 'Alta do Homecare'} />
          <KpiCard icon="🩹" label="Total de lesões"     value={kpis.totalLesoes}  color={C.orange}  sub="cadastradas" />
          <KpiCard icon="⚠️" label="Trocas em atraso"    value={kpis.trocasAtraso} color={C.red}    warn sub="curativo(s) vencido(s)"
            onClick={() => openDrill('Atraso', base.flatMap(p => p.lesoes.filter(l => l.dias !== null && l.dias < 0).map(l => ({ ...p, _lesao: l }))))}
            active={drillLabel === 'Atraso'} />
          <KpiCard icon="🕐" label="Trocas hoje/amanhã"  value={kpis.trocasHoje}   color={C.yellow} sub="≤ 1 dia"
            onClick={() => openDrill('Hoje', base.flatMap(p => p.lesoes.filter(l => l.dias !== null && l.dias >= 0 && l.dias <= 1).map(l => ({ ...p, _lesao: l }))))}
            active={drillLabel === 'Hoje'} />
          <KpiCard icon="📅" label="Trocas próx. 7 dias" value={kpis.trocas7d}     color={C.teal}   sub="nos próximos 7 dias" />
        </div>

        {/* ── Gráficos linha 1 ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 20 }}>

          {/* Situação */}
          <Section title="👤 Pacientes por situação">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={porSit} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => `${name.split(' ')[0]} ${value}`}
                  labelLine={false} style={{ fontSize: '0.7rem' }}>
                  {porSit.map((e, i) => <Cell key={i} fill={SIT_COLORS[e.name] || CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
          </Section>

          {/* Filial */}
          <Section title="🏠 Pacientes por filial">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={porFilial} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false} style={{ fontSize: '0.7rem' }}>
                  {porFilial.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
          </Section>

          {/* Programa */}
          <Section title="📋 Pacientes por programa">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={porPrograma} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false} style={{ fontSize: '0.7rem' }}>
                  {porPrograma.map((e, i) => <Cell key={i} fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
          </Section>

          {/* Interno x Externo */}
          <Section title="🚑 Lesões por origem (Interno x Externo)">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={porOrigem} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                  label={({ name, value }) => `${name} ${value}`}
                  labelLine={false} style={{ fontSize: '0.7rem' }}>
                  {porOrigem.map((e, i) => <Cell key={i} fill={ORIGEM_COLORS[e.name] || CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<Tip />} />
              </PieChart>
            </ResponsiveContainer>
          </Section>
        </div>

        {/* ── Gráficos linha 2 ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

          {/* Tipos de lesão */}
          <Section title="🔬 Lesões por tipo">
            <div style={{ height: Math.max(180, porTipo.length * 32) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porTipo} layout="vertical" barCategoryGap="30%" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 11, fill: C.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                  <Bar dataKey="value" name="Lesões" radius={[0, 4, 4, 0]} barSize={14}>
                    {porTipo.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          {/* Periodicidade */}
          <Section title="📆 Trocas por periodicidade">
            <div style={{ height: Math.max(180, porPeriodicidade.length * 44) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porPeriodicidade} layout="vertical" barCategoryGap="30%" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11, fill: C.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} cursor={{ fill: 'rgba(6,182,212,0.06)' }} />
                  <Bar dataKey="value" name="Lesões" radius={[0, 4, 4, 0]} barSize={18}>
                    {porPeriodicidade.map((_, i) => <Cell key={i} fill={[C.teal, C.blue, C.purple, C.orange, C.rose][i % 5]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>

        {/* ── Gráficos linha 3 ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

          {/* Operadora */}
          <Section title="🏢 Pacientes por operadora (top 10)">
            <div style={{ height: Math.max(180, porOperadora.length * 32) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porOperadora} layout="vertical" barCategoryGap="30%" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 11, fill: C.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
                  <Bar dataKey="value" name="Pacientes" radius={[0, 4, 4, 0]} barSize={14}>
                    {porOperadora.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          {/* Estádio da lesão */}
          <Section title="📊 Lesões por estádio">
            <div style={{ height: Math.max(180, porEstadio.length * 32) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={porEstadio} layout="vertical" barCategoryGap="30%" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 11, fill: C.text }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} cursor={{ fill: 'rgba(6,182,212,0.06)' }} />
                  <Bar dataKey="value" name="Lesões" radius={[0, 4, 4, 0]} barSize={14}>
                    {porEstadio.map((_, i) => <Cell key={i} fill={[C.teal, C.blue, C.purple, C.orange, C.rose, C.yellow][i % 6]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
        </div>

        {/* ── Evolução mensal ── */}
        {evolucaoMensal.length > 1 && (
          <Section title="📈 Evolução mensal — pacientes e lesões" style={{ marginBottom: 20 }}>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolucaoMensal} margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip />} />
                  <Legend wrapperStyle={{ fontSize: '0.78rem', color: C.muted }} />
                  <Line type="monotone" dataKey="pacientes" name="Pacientes" stroke={C.blue} strokeWidth={2.5} dot={{ r: 3, fill: C.blue }} />
                  <Line type="monotone" dataKey="lesoes"    name="Lesões"    stroke={C.orange} strokeWidth={2.5} dot={{ r: 3, fill: C.orange }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Section>
        )}

        {/* ── Trocas próximas ── */}
        {trocasProximas.length > 0 && (
          <Section title="⏰ Trocas de curativo nos próximos 7 dias" style={{ marginBottom: 20 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Paciente', 'Filial', 'Tipo de lesão', 'Periodicidade', 'Próxima troca', 'Status'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trocasProximas.map((row, i) => {
                    const color = row.dias < 0 ? C.red : row.dias === 0 ? C.yellow : row.dias <= 2 ? C.orange : C.teal;
                    const label = row.dias < 0 ? `${Math.abs(row.dias)}d em atraso` : row.dias === 0 ? 'Hoje' : `Em ${row.dias} dia(s)`;
                    return (
                      <tr key={i}>
                        <td style={S.td}>{row.pac}</td>
                        <td style={S.td}>{row.filial || '—'}</td>
                        <td style={S.td}>{row.tipo || '—'}</td>
                        <td style={S.td}>{row.periodicidade || '—'}</td>
                        <td style={S.td}>{fmtDate(row.proxima)}</td>
                        <td style={S.td}><Badge label={label} color={color} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* ── Drill-down de pacientes ── */}
        {drillLabel && drillRows.length > 0 && (
          <Section title={`📋 ${drillLabel} (${drillRows.length})`} style={{ marginBottom: 20 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Paciente', 'Filial', 'Programa', 'Operadora', 'Situação'].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drillRows.map((p, i) => (
                    <tr key={i}>
                      <td style={S.td}>{p.nome}</td>
                      <td style={S.td}>{nf(p.filial) || '—'}</td>
                      <td style={S.td}>{nf(p.programa_atual) || '—'}</td>
                      <td style={S.td}>{p.operadora || '—'}</td>
                      <td style={S.td}>
                        <Badge label={p.situacao || 'Em atendimento'} color={SIT_COLORS[p.situacao] || C.muted} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

      </div>
    </div>
  );
}
